import {
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import __ from "src/utils/Translations";
import {
  FC,
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { PROCESSES_SERVICE, restApiRequest } from "src/utils/Api";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { Link } from "react-router-dom";

interface ProcessData {
  authorId: string;
  id: string;
  status: string;
}

interface IPreviewImportCard {
  processData: ProcessData | undefined | { id: string };
  fields?: string[];
}

interface ProcessDetailsData {
  name?: string;
  status?: string;
  currentStep?: number;
  stepsOk?: number;
  stepsError?: number;
  stepsWarn?: number;
  maxStep?: number;
  createdAt?: string;
  updatedAt?: string;
}

const PreviewImportCard: FC<IPreviewImportCard> = ({
  processData,
  fields = [],
}) => {
  const [data, setData] = useState<ProcessDetailsData>({});
  const processId = processData?.id;
  const isAhr = useIsAhr();
  const companyId = useCompanyId();
  const { userInfo } = useContext(RbsContext);
  const userName = `${userInfo.getFirstName()} ${userInfo.getLastName()}`;

  const getStatusConfig = (status: string) => {
    const statusMap: Record<
      string,
      {
        color:
          | "default"
          | "primary"
          | "secondary"
          | "error"
          | "info"
          | "success"
          | "warning";
        label: string;
      }
    > = {
      new: {
        color: "default",
        label: "Nowy",
      },
      in_progress: {
        color: "info",
        label: "W trakcie",
      },
      success: {
        color: "success",
        label: "Zakończony sukcesem",
      },
      warning: {
        color: "warning",
        label: "Zakończony ostrzeżeniem",
      },
      failed: {
        color: "error",
        label: "Zakończony błędem",
      },
    };

    return statusMap[status] || { color: "default" as const, label: status };
  };

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCheckTimeout = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  const checkProcess = useCallback(async () => {
    clearCheckTimeout();

    const res = await restApiRequest(
      PROCESSES_SERVICE,
      `/ahr-processes/${processId}`,
      "GET",
      {}
    );

    setData(res);

    if (res.status === "new" || res.status === "in_progress") {
      timeout.current = setTimeout(checkProcess, 5000);
    } else if (res.status === "delegated") {
      timeout.current = setTimeout(checkProcess, 30000);
    }
  }, [processId, clearCheckTimeout]);

  useEffect(() => {
    checkProcess();
  }, [checkProcess]);

  useEffect(() => () => clearCheckTimeout(), [clearCheckTimeout]);

  const {
    name,
    status,
    currentStep,
    stepsOk,
    stepsError,
    stepsWarn,
    maxStep,
    createdAt,
    updatedAt,
  } = data;

  const statusConfig = getStatusConfig(status!);

  return (
    <Stack
      mt={2}
      data-testid="os__import-preview-card"
      sx={{
        backgroundColor: "#FAFAFA",
        border: "1px solid #808080",
        borderRadius: 4,
        padding: 3,
      }}
      flexDirection="column"
      gap={2}
    >
      <Stack
        flexDirection="row"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            textAlign: "start",
          }}
        >
          {__(`Podgląd importu: ${name}`)}
        </Typography>

        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography variant="body2">{__("Status")}</Typography>
          {(!status ||
            status === "new" ||
            status === "in_progress" ||
            status === "delegated") && <CircularProgress size={16} />}
          <Chip
            size="small"
            variant="outlined"
            label={statusConfig.label}
            color={statusConfig.color}
            sx={{
              bgcolor: (theme) =>
                statusConfig.color !== "default"
                  ? `${theme.palette[statusConfig.color].main}15`
                  : undefined,
            }}
          />
        </Stack>
      </Stack>

      <Stack
        flexDirection="row"
        gap={1}
        sx={{
          flexWrap: "wrap",
        }}
      >
        {fields.map((field) => (
          <Chip key={field} label={__(field)} />
        ))}
      </Stack>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
        }}
      >
        {__("Plik")}
        {": "}
        {name}
      </Typography>

      <Stack flexDirection={{ xs: "column", sm: "row" }} gap={3}>
        <Stack flex={1}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">{__("Wierszy ogółem")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {maxStep}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">{__("Obecny wiersz")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {currentStep}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">
              {__("Poprawnie przetworzone wiersze")}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {stepsOk}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">
              {__("Wiersze nieprzetworzone")}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {stepsWarn}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />
        </Stack>

        <Stack flex={1}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">{__("Błędy krytyczne")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {stepsError}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">{__("Data rozpoczęcia")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {createdAt
                ? format(new Date(createdAt), "dd.MM.yyyy, HH:mm:ss", {
                    locale: pl,
                  })
                : "—"}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">
              {__("Data ostatniej aktualizacji")}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {updatedAt
                ? format(new Date(updatedAt), "dd.MM.yyyy, HH:mm:ss", {
                    locale: pl,
                  })
                : "—"}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2">{__("Zlecający")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {userName}
            </Typography>
          </Stack>
          <Divider sx={{ marginY: "4px" }} />
        </Stack>
      </Stack>
      <Button
        variant="contained"
        data-testid="os__import-preview-details-button"
        sx={{ alignSelf: "flex-end" }}
        component={Link}
        to={
          isAhr
            ? `/ahr/imports-history/show/${processId}`
            : `/company/edit/${companyId}/imports-history/show/${processId}`
        }
      >
        {__("Zobacz szczegóły")}
      </Button>
    </Stack>
  );
};

export default PreviewImportCard;
